/* global Word */
/**
 * A ênfase DO CONTEÚDO (negrito, itálico, sublinhado, sobrescrito, subscrito)
 * sobrevivendo à troca do estilo de um parágrafo que já existe.
 *
 * Medido no Word (set/2026): definir `styleBuiltIn` apaga a formatação de
 * caractere do parágrafo inteiro — o termo em negrito no começo de um item, a
 * palavra em itálico no meio da frase, o "²" de "m²" (que vira "m2": mudança
 * de conteúdo, não de forma). Mesmo com o negrito cobrindo pouco do texto.
 * `startNewList`, `attachToList` e `detachFromList` preservam; só a troca de
 * estilo apaga. Aqui se tira um retrato das ênfases ANTES da troca e se
 * reaplica depois.
 *
 * O que é do TEXTO e o que é do ESTILO, propriedade por propriedade:
 *  - ênfase só em PARTE do parágrafo é do texto (estilo de parágrafo é
 *    uniforme) — guardada palavra a palavra;
 *  - ênfase no parágrafo INTEIRO só é do texto se o estilo de origem é de
 *    corpo. Num título o negrito é do estilo, numa citação o itálico: guardar
 *    esses deixaria o item de lista em negrito, o corpo inteiro em itálico.
 *
 * Granularidade: palavra; palavra com ênfase só em parte ("**Termo**:") é
 * dividida de novo na pontuação; e o que ainda sobra misturado ("m²", "CO₂",
 * "texto¹") é resolvido pelo OOXML do pedaço, que diz caractere a caractere o
 * que tem ênfase. O que nem assim se isola é contado, para o retorno avisar.
 */
import { wordSearchText } from './listPlan';
const PROPS = ['bold', 'italic', 'underline', 'superscript', 'subscript'];
const PALAVRA = [' ', '\t'];
const PONTUACAO = [':', ';', ',', '.', '!', '?', ')', '(', '"', '/', '-', '–', '—'];
// Estilos em que ênfase UNIFORME no parágrafo inteiro só pode ser formatação
// direta (o estilo não é negrito nem itálico).
const CORPO = new Set(['Normal', 'ListParagraph', 'NoSpacing', 'BodyText', 'PlainText']);
const FONT_PROPS = PROPS.map((p) => `font/${p}`).join(',');
const ITEM_FONT_PROPS = PROPS.map((p) => `items/font/${p}`).join(',');
function estado(f, prop) {
    if (prop === 'underline') {
        const u = f.underline == null ? '' : String(f.underline);
        if (u === 'Mixed')
            return 'mixed';
        return u && u !== 'None' ? 'on' : 'off';
    }
    const v = f[prop];
    if (v === null)
        return 'mixed';
    return v === true ? 'on' : 'off';
}
/** Marca com as propriedades `props` que estão LIGADAS nesta fonte. */
function marcaDe(range, f, props) {
    const m = { range };
    let alguma = false;
    for (const p of props) {
        if (estado(f, p) !== 'on')
            continue;
        alguma = true;
        if (p === 'underline')
            m.underline = String(f.underline);
        else
            m[p] = true;
    }
    return alguma ? m : null;
}
/** Retrato da ênfase de cada parágrafo (poucos syncs, qualquer que seja o número). */
export async function captureEmphasis(context, paras) {
    if (!paras.length)
        return [];
    paras.forEach((p) => p.load(`styleBuiltIn,${FONT_PROPS}`));
    await context.sync();
    // Por parágrafo: as propriedades em PARTE dele (guardar palavra a palavra) e
    // as ligadas nele INTEIRO num estilo de corpo (guardar o parágrafo todo).
    const partes = paras.map((p) => PROPS.filter((prop) => estado(p.font, prop) === 'mixed'));
    const inteiras = paras.map((p) => CORPO.has(String(p.styleBuiltIn)) ? PROPS.filter((prop) => estado(p.font, prop) === 'on') : []);
    const palavras = paras.map((p, i) => partes[i].length ? p.getRange('Content').split(PALAVRA, false, true, false) : null);
    palavras.forEach((c) => c === null || c === void 0 ? void 0 : c.load(ITEM_FONT_PROPS));
    if (palavras.some(Boolean))
        await context.sync();
    // Palavra com ênfase só em parte: divide de novo na pontuação.
    const pedacos = palavras.map((c, i) => c
        ? c.items.map((w) => partes[i].some((prop) => estado(w.font, prop) === 'mixed')
            ? w.split(PONTUACAO, false, true, false)
            : null)
        : []);
    // (sem `.flat()`: o alvo do build é ES2017)
    const todos = [];
    pedacos.forEach((lista) => lista.forEach((c) => c && todos.push(c)));
    todos.forEach((c) => c.load(ITEM_FONT_PROPS));
    if (todos.length)
        await context.sync();
    const snaps = paras.map((p, i) => {
        const marks = [];
        const inteira = marcaDe(p.getRange('Content'), p.font, inteiras[i]);
        if (inteira)
            marks.push(inteira);
        const misturados = [];
        const cols = palavras[i];
        if (cols) {
            cols.items.forEach((w, k) => {
                var _a, _b;
                const fontes = (_b = (_a = pedacos[i][k]) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : [w];
                fontes.forEach((r) => {
                    if (partes[i].some((prop) => estado(r.font, prop) === 'mixed'))
                        misturados.push(r);
                    const m = marcaDe(r, r.font, partes[i]);
                    if (m)
                        marks.push(m);
                });
            });
        }
        return { marks, misturados, props: partes[i] };
    });
    // Ênfase no meio de um pedaço sem pontuação: os runs do OOXML dizem onde.
    // (sem `.flatMap()`: o alvo do build é ES2017)
    const pendentes = [];
    snaps.forEach((sn, i) => sn.misturados.forEach((r) => pendentes.push({ i, r, props: sn.props })));
    const unresolved = paras.map(() => 0);
    if (pendentes.length) {
        const oxs = pendentes.map((x) => x.r.getOoxml());
        await context.sync();
        const buscas = pendentes.map((x, k) => {
            const trechos = trechosComEnfase(oxs[k].value || '', x.props);
            if (!trechos)
                return null;
            return trechos.map((t) => {
                const achados = x.r.search(wordSearchText(t.texto), { matchCase: true });
                achados.load('items');
                return { t, achados };
            });
        });
        if (buscas.some(Boolean))
            await context.sync();
        buscas.forEach((lista, k) => {
            const { i } = pendentes[k];
            if (!lista) {
                unresolved[i]++;
                return;
            }
            for (const { t, achados } of lista) {
                const alvo = achados.items[t.ocorrencia];
                if (alvo)
                    snaps[i].marks.push(Object.assign({ range: alvo }, t.enfase));
                else
                    unresolved[i]++;
            }
        });
    }
    return snaps.map((sn, i) => ({ marks: sn.marks, unresolved: unresolved[i] }));
}
const XML_ENTIDADES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const decodificar = (t) => t.replace(/&(amp|lt|gt|quot|apos);/g, (_, e) => XML_ENTIDADES[e]);
/** Liga/desliga de uma propriedade booleana no `<w:rPr>` (`<w:b/>`, `<w:b w:val="0"/>`). */
function ligado(rPr, tag) {
    var _a;
    const m = new RegExp(`<w:${tag}(?=[\\s/>])([^>]*)/?>`).exec(rPr);
    if (!m)
        return false;
    const val = (_a = /w:val="([^"]*)"/.exec(m[1])) === null || _a === void 0 ? void 0 : _a[1];
    return val === undefined || !/^(0|false|off)$/i.test(val);
}
/**
 * Trechos com ênfase dentro de um pedaço, a partir dos runs do OOXML dele:
 * texto, qual ocorrência daquele texto no pedaço, e a ênfase (só as
 * propriedades em `props`). `null` quando o OOXML não dá para ler.
 */
function trechosComEnfase(ooxml, props) {
    var _a, _b, _c, _d, _e, _f;
    const parte = /pkg:name="\/word\/document\.xml"[\s\S]*?<\/pkg:part>/i.exec(ooxml);
    const corpo = parte && /<w:body>([\s\S]*)<\/w:body>/i.exec(parte[0]);
    if (!corpo)
        return null;
    let tudo = '';
    const trechos = [];
    for (const run of (_a = corpo[1].match(/<w:r[\s>][\s\S]*?<\/w:r>/g)) !== null && _a !== void 0 ? _a : []) {
        const texto = decodificar(((_b = run.match(/<w:t(?:\s[^>]*)?>[\s\S]*?<\/w:t>|<w:tab\/>/g)) !== null && _b !== void 0 ? _b : [])
            .map((t) => (t === '<w:tab/>' ? '\t' : t.replace(/<[^>]+>/g, '')))
            .join(''));
        if (!texto)
            continue;
        const rPr = (_d = (_c = /<w:rPr>([\s\S]*?)<\/w:rPr>/.exec(run)) === null || _c === void 0 ? void 0 : _c[1]) !== null && _d !== void 0 ? _d : '';
        const enfase = {};
        if (props.includes('bold') && ligado(rPr, 'b'))
            enfase.bold = true;
        if (props.includes('italic') && ligado(rPr, 'i'))
            enfase.italic = true;
        const u = (_e = /<w:u\s+w:val="([^"]+)"/.exec(rPr)) === null || _e === void 0 ? void 0 : _e[1];
        if (props.includes('underline') && u && u !== 'none')
            enfase.underline = u.charAt(0).toUpperCase() + u.slice(1);
        const vert = (_f = /<w:vertAlign\s+w:val="([^"]+)"/.exec(rPr)) === null || _f === void 0 ? void 0 : _f[1];
        if (props.includes('superscript') && vert === 'superscript')
            enfase.superscript = true;
        if (props.includes('subscript') && vert === 'subscript')
            enfase.subscript = true;
        if (Object.keys(enfase).length && texto.trim()) {
            // Qual ocorrência deste texto no pedaço (o "2" de "m2" é o 1º "2").
            let ocorrencia = 0;
            for (let at = tudo.indexOf(texto); at >= 0; at = tudo.indexOf(texto, at + 1))
                ocorrencia++;
            trechos.push({ texto, ocorrencia, enfase });
        }
        tudo += texto;
    }
    return trechos;
}
/**
 * Reaplica o retrato (sem sync — vai no lote de quem chama). `skipBold` /
 * `skipItalic`: quando o estilo NOVO define aquilo (citação é itálico), o do
 * estilo manda.
 */
export function restoreEmphasis(snap, opts = {}) {
    snap.marks.forEach((m) => {
        const f = m.range.font;
        if (m.bold && !opts.skipBold)
            f.bold = true;
        if (m.italic && !opts.skipItalic)
            f.italic = true;
        if (m.underline)
            f.underline = m.underline;
        if (m.superscript)
            f.superscript = true;
        if (m.subscript)
            f.subscript = true;
    });
}
